'use client'

import { ReactNode, useState, useTransition, useCallback } from 'react'
import { ProductVariantKind } from '@productsite-beta/src/graphql/generated/graphql-types'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import ProductVariantGroup from '../../components/product-variant-group/product-variant-group'
import type {
  VariantGroupData,
  VariantValue,
} from '../../components/product-variant-group/product-variant-group.types'
import dynamic from 'next/dynamic'

export type {
  VariantValue,
  VariantGroupData,
} from '../../components/product-variant-group/product-variant-group.types'

const ProductVariantModal = dynamic(() => import('../../components/product-variant-modal'))

const expandedGroups = new Set<string>()

type Props = {
  variantGroups: VariantGroupData[]
  currentProductId: string
  children: ReactNode
}

const ProductOptionsLayoutClient = ({ variantGroups, currentProductId, children }: Props) => {
  const [isPending, startTransition] = useTransition()
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const [modalState, setModalState] = useState<{
    show: boolean
    changedVariantNames: string[]
    confirmUrl: string
  }>({ show: false, changedVariantNames: [], confirmUrl: '' })

  const handleNavigate = useCallback((groupName: string) => {
    setActiveGroup(groupName)
  }, [])

  const handleExpandChange = useCallback((groupName: string, expanded: boolean) => {
    if (expanded) {
      expandedGroups.add(groupName)
    } else {
      expandedGroups.delete(groupName)
    }
  }, [])

  const handleUnavailableSelect = useCallback((value: VariantValue) => {
    setModalState({
      show: true,
      changedVariantNames: value.missingVariantNames ?? [],
      confirmUrl: getProductUrl(value.productErpIdentifier || '', value.product?.nameSlug || ''),
    })
  }, [])

  const activeGroupData = variantGroups.find(g => g.variantName === activeGroup)
  const isActiveGroupImage = activeGroupData?.kind === ProductVariantKind.ProductImage

  return (
    <>
      <div className="d-flex gap-24 flex-direction-column">
        {variantGroups.map((group, i) => {
          if (group.values.length === 1) {
            return null
          }

          return (
            <ProductVariantGroup
              key={group.variantName + i}
              variantName={group.variantName}
              variantNameEng={group.variantNameEng}
              variantKind={group.kind}
              variantValues={group.values}
              currentProductId={currentProductId}
              isPending={isPending}
              isActiveGroup={activeGroup === group.variantName}
              isActiveGroupImage={isActiveGroupImage}
              defaultExpanded={expandedGroups.has(group.variantName)}
              startTransition={startTransition}
              onNavigate={handleNavigate}
              onExpandChange={expanded => handleExpandChange(group.variantName, expanded)}
              onUnavailableSelect={handleUnavailableSelect}
            />
          )
        })}
      </div>
      {children}
      <ProductVariantModal
        show={modalState.show}
        onClose={() => setModalState(s => ({ ...s, show: false }))}
        changedVariantNames={modalState.changedVariantNames}
        confirmUrl={modalState.confirmUrl}
      />
    </>
  )
}

export default ProductOptionsLayoutClient
